import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Autocomplete,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import api from '../services/api';

const FeasibilityAnalysis = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [formData, setFormData] = useState({
    project_id: '',
    requesting_department: '',
    requester_name: '',
    problem_description: '',
    project_objectives: '',
    is_technically_feasible: false,
    technologies_used: '',
    technical_limitations: '',
    requires_integrations: false,
    integration_systems: '',
    problem_solved: '',
    benefited_sectors: '',
    workflow_impact: '',
    has_simpler_alternative: false,
    alternative_description: '',
    development_days: 0,
    testing_days: 0,
    documentation_days: 0,
    deployment_days: 0,
    training_days: 0,
    required_team_members: 0,
    requires_ongoing_support: false,
    development_cost: 0,
    infrastructure_cost: 0,
    training_documentation_cost: 0,
    support_maintenance_cost: 0,
    project_risks: '',
    risk_mitigation_strategies: '',
    contingency_plan: '',
    aligns_with_company_goals: false,
    implementation_urgency: '',
    has_significant_impact_if_delayed: false,
    decision: '',
    decision_justification: '',
    start_date: '',
    expected_end_date: '',
    priority: '',
    responsible_persons: '',
    estimated_budget: 0
  });
  const [technicalResponsibles, setTechnicalResponsibles] = useState([]);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return false;
    }
    return token;
  };

  const checkUserRole = async () => {
    try {
      const response = await api.get('/users/me');
      if (response.data) {
        // Verificar se é admin ou técnico
        const isAdminOrTechnical = response.data.role_id === 1 || response.data.role_id === 3;
        setIsAuthorized(isAdminOrTechnical);
        if (!isAdminOrTechnical) {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar permissões do usuário:', error);
      navigate('/dashboard');
    }
  };

  const fetchTechnicalResponsibles = async () => {
    try {
      const response = await api.get('/technical-responsibles/');
      const activeUsers = response.data.filter(user => user.is_active);
      setTechnicalResponsibles(activeUsers);
      console.log('Responsáveis técnicos carregados:', activeUsers);
    } catch (err) {
      console.error('Erro ao carregar responsáveis técnicos:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      const token = checkAuth();
      if (token) {
        await checkUserRole();
        await fetchAnalyses();
        await fetchProjects();
        await fetchTechnicalResponsibles();
      }
    };
    init();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await api.get('/feasibility-analysis/');
      setAnalyses(response.data);
    } catch (err) {
      setError('Erro ao carregar as análises de viabilidade');
      console.error('Erro ao carregar análises:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProjects = async () => {
    try {
      // Buscar projetos para o dropdown de seleção no formulário
      const response = await api.get('/projects/');
      setProjects(response.data);
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
    }
  };

  const handleOpenDialog = (analysis = null) => {
    if (analysis) {
      setSelectedAnalysis(analysis);
      
      // Converter string de responsáveis para objetos de usuário
      let responsiblePersons = [];
      if (analysis.responsible_persons) {
        const responsibleNames = analysis.responsible_persons.split(',').map(name => name.trim());
        responsiblePersons = responsibleNames.map(name => {
          // Tentar encontrar o usuário correspondente pelo nome
          const user = technicalResponsibles.find(tech => tech.full_name === name);
          return user || name; // Se não encontrar, manter o nome como string
        });
      }
      
      setSelectedResponsibles(responsiblePersons);
      setFormData({
        project_id: analysis.project_id,
        requesting_department: analysis.requesting_department,
        requester_name: analysis.requester_name,
        problem_description: analysis.problem_description,
        project_objectives: analysis.project_objectives,
        is_technically_feasible: analysis.is_technically_feasible,
        technologies_used: analysis.technologies_used,
        technical_limitations: analysis.technical_limitations,
        requires_integrations: analysis.requires_integrations,
        integration_systems: analysis.integration_systems,
        problem_solved: analysis.problem_solved,
        benefited_sectors: analysis.benefited_sectors,
        workflow_impact: analysis.workflow_impact,
        has_simpler_alternative: analysis.has_simpler_alternative,
        alternative_description: analysis.alternative_description,
        development_days: analysis.development_days,
        testing_days: analysis.testing_days,
        documentation_days: analysis.documentation_days,
        deployment_days: analysis.deployment_days,
        training_days: analysis.training_days,
        required_team_members: analysis.required_team_members,
        requires_ongoing_support: analysis.requires_ongoing_support,
        development_cost: analysis.development_cost,
        infrastructure_cost: analysis.infrastructure_cost,
        training_documentation_cost: analysis.training_documentation_cost,
        support_maintenance_cost: analysis.support_maintenance_cost,
        project_risks: analysis.project_risks,
        risk_mitigation_strategies: analysis.risk_mitigation_strategies,
        contingency_plan: analysis.contingency_plan,
        aligns_with_company_goals: analysis.aligns_with_company_goals,
        implementation_urgency: analysis.implementation_urgency,
        has_significant_impact_if_delayed: analysis.has_significant_impact_if_delayed,
        decision: analysis.decision,
        decision_justification: analysis.decision_justification,
        start_date: analysis.start_date,
        expected_end_date: analysis.expected_end_date,
        priority: analysis.priority,
        responsible_persons: analysis.responsible_persons,
        estimated_budget: analysis.estimated_budget
      });
    } else {
      setSelectedAnalysis(null);
      setSelectedResponsibles([]);
      setFormData({
        project_id: '',
        requesting_department: '',
        requester_name: '',
        problem_description: '',
        project_objectives: '',
        is_technically_feasible: false,
        technologies_used: '',
        technical_limitations: '',
        requires_integrations: false,
        integration_systems: '',
        problem_solved: '',
        benefited_sectors: '',
        workflow_impact: '',
        has_simpler_alternative: false,
        alternative_description: '',
        development_days: 0,
        testing_days: 0,
        documentation_days: 0,
        deployment_days: 0,
        training_days: 0,
        required_team_members: 0,
        requires_ongoing_support: false,
        development_cost: 0,
        infrastructure_cost: 0,
        training_documentation_cost: 0,
        support_maintenance_cost: 0,
        project_risks: '',
        risk_mitigation_strategies: '',
        contingency_plan: '',
        aligns_with_company_goals: false,
        implementation_urgency: '',
        has_significant_impact_if_delayed: false,
        decision: '',
        decision_justification: '',
        start_date: '',
        expected_end_date: '',
        priority: '',
        responsible_persons: '',
        estimated_budget: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAnalysis(null);
  };

  const handleResponsiblesChange = (event, newValue) => {
    setSelectedResponsibles(newValue);
    
    // Converter para string formatada para o formData
    const responsibleString = newValue.map(item => 
      typeof item === 'string' ? item : item.full_name
    ).join(', ');
    
    setFormData(prev => ({
      ...prev,
      responsible_persons: responsibleString
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Garantir que responsible_persons seja uma string
      const responsibleString = selectedResponsibles.map(item => 
        typeof item === 'string' ? item : item.full_name
      ).join(', ');
      
      const dataToSubmit = {
        ...formData,
        responsible_persons: responsibleString
      };
      
      console.log('Enviando dados:', dataToSubmit);
      
      if (selectedAnalysis) {
        await api.put(`/feasibility-analysis/${selectedAnalysis.project_id}`, dataToSubmit);
      } else {
        await api.post('/feasibility-analysis/', dataToSubmit);
      }
      handleCloseDialog();
      fetchAnalyses();
    } catch (err) {
      setError('Erro ao salvar a análise de viabilidade');
      console.error('Erro ao salvar análise:', err);
    }
  };

  const handleDelete = async (projectId) => {
    if (window.confirm('Tem certeza que deseja excluir esta análise de viabilidade?')) {
      try {
        await api.delete(`/feasibility-analysis/${projectId}`);
        fetchAnalyses();
      } catch (err) {
        setError('Erro ao excluir a análise de viabilidade');
        console.error('Erro ao excluir análise:', err);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthorized) {
    return (
      <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 }, mt: 2, mb: 4 }}>
        <Alert severity="error">
          Você não tem permissão para acessar esta página.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, md: 2 }, mt: 2, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Análise de Viabilidade</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nova Análise
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Projeto</TableCell>
              <TableCell>Departamento</TableCell>
              <TableCell>Prioridade</TableCell>
              <TableCell>Decisão</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analyses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhuma análise de viabilidade encontrada
                </TableCell>
              </TableRow>
            ) : (
              analyses.map((analysis) => (
                <TableRow key={analysis.analysis_id}>
                  <TableCell>{analysis.project_name}</TableCell>
                  <TableCell>{analysis.requesting_department}</TableCell>
                  <TableCell>{analysis.priority}</TableCell>
                  <TableCell>{analysis.decision}</TableCell>
                  <TableCell>
                    <Tooltip title="Visualizar">
                      <IconButton onClick={() => handleOpenDialog(analysis)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton onClick={() => handleOpenDialog(analysis)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton onClick={() => handleDelete(analysis.project_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAnalysis ? 'Editar Análise de Viabilidade' : 'Nova Análise de Viabilidade'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            {/* Seção de Informações Básicas */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Informações Básicas
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="Projeto"
                  name="project_id"
                  value={formData.project_id}
                  onChange={handleChange}
                  required
                >
                  {projects.map((project) => (
                    <MenuItem key={project.project_id} value={project.project_id}>
                      {project.project_name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Departamento Solicitante"
                  name="requesting_department"
                  value={formData.requesting_department}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Nome do Solicitante"
                  name="requester_name"
                  value={formData.requester_name}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Descrição do Problema"
                  name="problem_description"
                  value={formData.problem_description}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Objetivos do Projeto"
                  name="project_objectives"
                  value={formData.project_objectives}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>

            {/* Seção de Viabilidade Técnica */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Viabilidade Técnica
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.is_technically_feasible}
                      onChange={handleChange}
                      name="is_technically_feasible"
                    />
                  }
                  label="Tecnicamente Viável"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tecnologias Utilizadas"
                  name="technologies_used"
                  value={formData.technologies_used}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Limitações Técnicas"
                  name="technical_limitations"
                  value={formData.technical_limitations}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requires_integrations}
                      onChange={handleChange}
                      name="requires_integrations"
                    />
                  }
                  label="Requer Integrações"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sistemas de Integração"
                  name="integration_systems"
                  value={formData.integration_systems}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* Seção de Impacto e Benefícios */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Impacto e Benefícios
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Problema Resolvido"
                  name="problem_solved"
                  value={formData.problem_solved}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Setores Beneficiados"
                  name="benefited_sectors"
                  value={formData.benefited_sectors}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Impacto no Fluxo de Trabalho"
                  name="workflow_impact"
                  value={formData.workflow_impact}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.has_simpler_alternative}
                      onChange={handleChange}
                      name="has_simpler_alternative"
                    />
                  }
                  label="Existe Alternativa Mais Simples"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descrição da Alternativa"
                  name="alternative_description"
                  value={formData.alternative_description}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* Seção de Recursos e Prazos */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Recursos e Prazos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias de Desenvolvimento"
                  name="development_days"
                  value={formData.development_days}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias de Teste"
                  name="testing_days"
                  value={formData.testing_days}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias de Documentação"
                  name="documentation_days"
                  value={formData.documentation_days}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias de Implantação"
                  name="deployment_days"
                  value={formData.deployment_days}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Dias de Treinamento"
                  name="training_days"
                  value={formData.training_days}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Membros da Equipe"
                  name="required_team_members"
                  value={formData.required_team_members}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.requires_ongoing_support}
                      onChange={handleChange}
                      name="requires_ongoing_support"
                    />
                  }
                  label="Requer Suporte Contínuo"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={technicalResponsibles}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.full_name}
                  value={selectedResponsibles}
                  onChange={handleResponsiblesChange}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        variant="outlined"
                        label={typeof option === 'string' ? option : option.full_name}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  isOptionEqualToValue={(option, value) => 
                    (typeof option === 'string' && typeof value === 'string' && option === value) ||
                    (typeof option === 'object' && typeof value === 'string' && option.full_name === value) ||
                    (typeof option === 'string' && typeof value === 'object' && option === value.full_name) ||
                    (typeof option === 'object' && typeof value === 'object' && option.responsible_id === value.responsible_id)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Pessoas Responsáveis"
                      placeholder="Selecione pessoas responsáveis"
                      required
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Seção de Custos */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Custos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Custo de Desenvolvimento"
                  name="development_cost"
                  value={formData.development_cost}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>R$</span>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Custo de Infraestrutura"
                  name="infrastructure_cost"
                  value={formData.infrastructure_cost}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>R$</span>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Custo de Treinamento e Documentação"
                  name="training_documentation_cost"
                  value={formData.training_documentation_cost}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>R$</span>,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Custo de Suporte e Manutenção"
                  name="support_maintenance_cost"
                  value={formData.support_maintenance_cost}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>R$</span>,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label="Orçamento Estimado Total"
                  name="estimated_budget"
                  value={formData.estimated_budget}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>R$</span>,
                  }}
                />
              </Grid>
            </Grid>

            {/* Seção de Riscos */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Riscos
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Riscos do Projeto"
                  name="project_risks"
                  value={formData.project_risks}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Estratégias de Mitigação de Riscos"
                  name="risk_mitigation_strategies"
                  value={formData.risk_mitigation_strategies}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Plano de Contingência"
                  name="contingency_plan"
                  value={formData.contingency_plan}
                  onChange={handleChange}
                  required
                />
              </Grid>
            </Grid>

            {/* Seção de Decisão e Cronograma */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
              Decisão e Cronograma
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.aligns_with_company_goals}
                      onChange={handleChange}
                      name="aligns_with_company_goals"
                    />
                  }
                  label="Alinha-se com os Objetivos da Empresa"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.has_significant_impact_if_delayed}
                      onChange={handleChange}
                      name="has_significant_impact_if_delayed"
                    />
                  }
                  label="Impacto Significativo se Atrasado"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Urgência de Implementação"
                  name="implementation_urgency"
                  value={formData.implementation_urgency}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Baixa">Baixa</MenuItem>
                  <MenuItem value="Média">Média</MenuItem>
                  <MenuItem value="Alta">Alta</MenuItem>
                  <MenuItem value="Crítica">Crítica</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Prioridade"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Baixa">Baixa</MenuItem>
                  <MenuItem value="Média">Média</MenuItem>
                  <MenuItem value="Alta">Alta</MenuItem>
                  <MenuItem value="Crítica">Crítica</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Decisão"
                  name="decision"
                  value={formData.decision}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="Aprovado">Aprovado</MenuItem>
                  <MenuItem value="Reprovado">Reprovado</MenuItem>
                  <MenuItem value="Em análise">Em análise</MenuItem>
                  <MenuItem value="Aguardando informações">Aguardando informações</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Justificativa da Decisão"
                  name="decision_justification"
                  value={formData.decision_justification}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Início"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de Término Esperada"
                  name="expected_end_date"
                  value={formData.expected_end_date}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeasibilityAnalysis; 